'use client';

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Box } from '@lucid/lego';
import type { FormField } from '@lucid/lego';
import type { Theme, VehicleData } from './types';
import { getVehicleImageUrl } from './utils';
import vehiclesData from '../../data/vehicleHub/vehicles.json';
import SearchInput from './SearchInput/SearchInput';
import SearchResults from './SearchResults/SearchResults';
import VehicleDetailsSlider from './VehicleDetailsSlider/VehicleDetailsSlider';
import styles from './VinSearch.module.scss';

const DEFAULT_DEBOUNCE_DELAY = 300;

// Raw vehicle data type from JSON (without image)
type RawVehicleData = Omit<VehicleData, 'vehicle'> & {
  vehicle: Omit<VehicleData['vehicle'], 'image'> & { image?: unknown };
};

// Transform JSON data to include actual image paths from Lucid CDN
const mockVehicles: VehicleData[] = (vehiclesData as RawVehicleData[]).map((vehicle) => ({
  ...vehicle,
  vehicle: {
    ...vehicle.vehicle,
    image: getVehicleImageUrl(vehicle.vehicle.model, vehicle.vehicle.imageColor || 'white'),
  },
}));

type VinSearchProps = {
  /** Theme for the component @default 'dark' */
  theme?: Theme;
  /** Label text above the search input @default 'Locate Asset' */
  label?: string;
  /** Placeholder text for the search input */
  placeholder?: string;
  /** Message to show when no results found @default 'No vehicles found' */
  emptyMessage?: string;
  /** Custom className for the container */
  className?: string;
  /** Test ID for testing */
  dataTestId?: string;
  /** Debounce delay in milliseconds @default 300 */
  debounceDelay?: number;
  /** Title for the details slider */
  sliderTitle?: string;
  /** Function to generate form fields based on selected asset */
  getFormFields?: (selectedAsset: VehicleData) => FormField[];
  /** Title for the form accordion */
  formTitle?: string;
  /** Form ID */
  formId?: string;
  /** Whether form is submitting */
  isSubmitting?: boolean;
  /** Success message to display in form */
  successMessage?: string;
  /** Error message to display in form */
  errorMessage?: string | null;
  /** Callback when form submits successfully - receives form data and selected asset */
  onFormSubmit?: (formData: unknown, selectedAsset: VehicleData) => void;
  /** Callback when form has validation errors */
  onFormError?: (error: unknown) => void;
  /** Callback when slider closes */
  onSliderClose?: () => void;
};

/**
 * VinSearch provides a complete VIN search interface.
 * Includes search input, results display, and details slider with optional form.
 * Handles all search logic internally using mock data.
 *
 * @component
 * @example
 * // Without form (view only)
 * <VinSearch
 *   theme="dark"
 *   sliderTitle="Vehicle Details"
 * />
 *
 * @example
 * // With form
 * <VinSearch
 *   theme="dark"
 *   sliderTitle="Create Case"
 *   getFormFields={(asset) => createFormFields(asset)}
 *   formTitle="Case Details"
 *   onFormSubmit={(data, asset) => handleSubmit(data, asset)}
 *   onFormError={(error) => console.error(error)}
 * />
 */
const VinSearch: React.FC<VinSearchProps> = ({
  theme = 'dark',
  label = 'Locate Asset',
  placeholder = 'Search: VIN / Name / Tel No. / Licence Plate ...',
  emptyMessage = 'No vehicles found',
  className,
  dataTestId = 'vin-search',
  debounceDelay = DEFAULT_DEBOUNCE_DELAY,
  sliderTitle = 'Vehicle Details',
  getFormFields,
  formTitle = 'Form Details',
  formId = 'vin-search-form',
  isSubmitting = false,
  successMessage = '',
  errorMessage = null,
  onFormSubmit,
  onFormError,
  onSliderClose,
}) => {
  const invertedTheme = theme === 'dark' ? 'light' : 'dark';
  const containerRef = useRef<HTMLDivElement>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<VehicleData[]>([]);

  // Slider state
  const [selectedAsset, setSelectedAsset] = useState<VehicleData | null>(null);
  const [isSliderOpen, setIsSliderOpen] = useState(false);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, debounceDelay);

    return () => clearTimeout(timer);
  }, [searchQuery, debounceDelay]);

  // Filter mock data based on debounced query
  // TODO: Replace with API hook when backend is ready
  useEffect(() => {
    if (!debouncedQuery.trim()) {
      setResults([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const timer = setTimeout(() => {
      const searchLower = debouncedQuery.toLowerCase();
      const filtered = mockVehicles.filter(
        (v) =>
          v.details.vin.toLowerCase().includes(searchLower) ||
          v.customer.name.toLowerCase().includes(searchLower) ||
          v.customer.phone.includes(debouncedQuery) ||
          v.vehicle.model.toLowerCase().includes(searchLower)
      );
      setResults(filtered);
      setIsLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [debouncedQuery]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && searchQuery.trim()) {
        setDebouncedQuery(searchQuery);
      }
    },
    [searchQuery]
  );

  const handleCardClick = useCallback((asset: VehicleData) => {
    setSelectedAsset(asset);
    setIsSliderOpen(true);
  }, []);

  const handleCloseSlider = useCallback(() => {
    setIsSliderOpen(false);
    setSelectedAsset(null);
    onSliderClose?.();
  }, [onSliderClose]);

  const handleFormSuccess = useCallback(
    (data: unknown) => {
      if (selectedAsset && onFormSubmit) {
        onFormSubmit(data, selectedAsset);
      }
    },
    [selectedAsset, onFormSubmit]
  );

  const handleFormError = useCallback(
    (error: unknown) => {
      onFormError?.(error);
    },
    [onFormError]
  );

  // Generate form fields dynamically based on selected asset
  const resolvedFormFields =
    selectedAsset && getFormFields ? getFormFields(selectedAsset) : undefined;

  const hasSearched = debouncedQuery.trim().length > 0;

  return (
    <Box className={`${styles.container} ${className || ''}`} data-testid={dataTestId}>
      <SearchInput
        theme={theme}
        label={label}
        placeholder={placeholder}
        value={searchQuery}
        onChange={handleSearchChange}
        onKeyDown={handleKeyDown}
        dataTestId={dataTestId}
      />

      <SearchResults
        theme={theme}
        results={results}
        isLoading={isLoading}
        hasSearched={hasSearched}
        emptyMessage={emptyMessage}
        onCardClick={handleCardClick}
        dataTestId={dataTestId}
      />

      <VehicleDetailsSlider
        isOpen={isSliderOpen}
        onClose={handleCloseSlider}
        title={sliderTitle}
        theme={invertedTheme}
        selectedAsset={selectedAsset}
        formFields={resolvedFormFields}
        formTitle={formTitle}
        formId={formId}
        successMessage={successMessage}
        errorMessage={errorMessage}
        isSubmitting={isSubmitting}
        onFormSuccess={handleFormSuccess}
        onFormError={handleFormError}
        containerRef={containerRef}
      />
    </Box>
  );
};

export default VinSearch;
